import React from 'react';
import { useNavigate } from 'react-router-dom';
import { T, RADIUS, BODY_FONT } from '../messagesModel';
import { fmtWhen, publishedLine } from './planModel';

/** `SAT OCT 10 · 6:00 PM → 10:00 PM` from the card's UTC moments, in the viewer's clock. */
function whenLine(card) {
  if (!card?.startUtc) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const local = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
  const start = fmtWhen(local(card.startUtc));
  if (!card.endUtc) return start;
  const e = new Date(card.endUtc);
  const h = e.getHours(); const m = e.getMinutes();
  return `${start} → ${h % 12 || 12}${m ? `:${pad(m)}` : ':00'} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * The published card (board 1e): the first thing anyone else in the chat sees. A centred line
 * (`YOU OPENED A PORTAL FROM THIS CHAT`), then a 340px white card from the sender: a 140px cover,
 * a PORTAL / EVENT pill, the title, when, where (`PLACE SHOWS ONCE YOU'RE IN` under a venue
 * lock), a meta line and a 40px black CTA — I'M IN · RSVP · GET TICKET · GET TICKETS ↗.
 */
export default function PlanCard({ msg, mine = false }) {
  const navigate = useNavigate();
  const card = msg?.plan;
  if (!card) return null;
  const senderFirst = (msg.senderDisplayName || '').split(' ')[0].toUpperCase();
  const line = mine ? publishedLine(card.kind, true) : `${senderFirst || 'SOMEONE'} ${card.kind === 'event' ? 'CREATED AN EVENT' : 'OPENED A PORTAL'} FROM THIS CHAT`;
  const open = (e) => {
    e.stopPropagation();
    if (card.externalUrl) { window.open(card.externalUrl, '_blank', 'noopener'); return; }
    navigate(card.href);
  };
  return (
    <div data-plan-card data-kind={card.kind} style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', width: '100%' }}>
      <div className="font-mono" style={{ alignSelf: 'center', margin: '14px 0 6px', fontSize: 10, letterSpacing: '.1em', color: T.grey500, textAlign: 'center' }}>{line}</div>
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter') open(e); }}
        style={{ width: 340, maxWidth: '100%', background: T.white, borderRadius: RADIUS.bubble, overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer', boxSizing: 'border-box' }}
      >
        <div style={{ height: 140, background: card.coverUrl ? `url(${card.coverUrl}) center/cover` : 'repeating-linear-gradient(135deg,#ECECEC 0 10px,#F5F5F5 10px 20px)', display: 'flex', alignItems: 'flex-end', padding: 12 }}>
          {!card.coverUrl && <span className="font-mono" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: T.grey500 }}>COVER</span>}
        </div>
        <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="font-mono" style={{ alignSelf: 'flex-start', height: 22, padding: '0 10px', borderRadius: RADIUS.pill, background: T.ink, color: T.white, fontSize: 9, fontWeight: 700, letterSpacing: '.12em', display: 'flex', alignItems: 'center' }}>{card.kind === 'event' ? 'EVENT' : 'PORTAL'}</span>
          <span style={{ fontFamily: BODY_FONT, fontSize: 18, fontWeight: 600, lineHeight: 1.25 }}>{card.title}</span>
          {whenLine(card) && <span className="font-mono" style={{ fontSize: 11, letterSpacing: '.08em', color: T.grey600 }}>{whenLine(card)}</span>}
          {(card.placeHidden || card.where) && (
            <span className="font-mono" style={{ fontSize: 11, letterSpacing: '.08em', color: T.grey600 }}>{card.placeHidden ? 'PLACE SHOWS ONCE YOU’RE IN' : (card.where || '').toUpperCase()}</span>
          )}
          {card.meta && <span className="font-mono" style={{ fontSize: 11, letterSpacing: '.08em', color: T.grey600 }}>{card.meta}</span>}
          <button type="button" onClick={open} className="font-mono hover:bg-white hover:text-black" style={{ marginTop: 6, height: 40, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.ink, color: T.white, fontSize: 11, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer', transition: 'background-color 200ms, color 200ms' }}>{card.cta}</button>
        </div>
      </div>
    </div>
  );
}
